import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { CloudUpload, ErrorOutline, FileUpload } from "@mui/icons-material";

interface DragNDropFileProps {
  maxFileSizeMB: number;
  allowedExtensions: string[];
  onFileUploaded: (file: File) => void;
}

export const DragNDropFile: React.FC<DragNDropFileProps> = ({
  maxFileSizeMB,
  allowedExtensions,
  onFileUploaded,
}) => {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateFile(e.dataTransfer.files[0]);
    }
  };

  const validateFile = (file: File) => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const fileSizeMB = file.size / (1024 * 1024);

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      setError(
        `Invalid file type. Allowed types: ${allowedExtensions.join(", ")}`,
      );
      setFile(null);
      return;
    }

    if (fileSizeMB > maxFileSizeMB) {
      setError(`File size exceeds the limit of ${maxFileSizeMB} MB.`);
      setFile(null);
      return;
    }

    setError(null);
    setFile(file);
    onFileUploaded(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateFile(e.target.files[0]);
    }
  };

  return (
    <Box>
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          border: "1px dashed #ccc",
          padding: "20px",
          textAlign: "center",
          backgroundColor: dragging ? "#d9d9d9" : "#f5f5f5",
          borderRadius: "8px",
          cursor: "pointer",
          transition: "background-color 0.3s",
        }}
      >
        <CloudUpload
          fontSize="large"
          sx={{ color: dragging ? "#aaa" : "#ccc" }}
        />
        <Typography variant="body2" my={2} color="text.secondary">
          Drag and drop your file here, or click to button to browse your file
        </Typography>
        <Button
          component="label"
          variant="contained"
          color="primary"
          size="small"
          startIcon={<FileUpload />}
        >
          Choose File
          <input
            type="file"
            hidden
            onChange={handleFileSelect}
            accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
          />
        </Button>
        {file && (
          <Box>
            <Typography variant="body1" mt={1}>
              File uploaded: <strong>{file.name}</strong>{" "}
            </Typography>
            <Typography component="span" variant="body2" color="textSecondary">
              Size: {(file.size / (1024 * 1024)).toFixed(2)} MB
            </Typography>
          </Box>
        )}
        {error && (
          <Typography color="error" mt={2}>
            <ErrorOutline fontSize="small" /> {error}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="caption" color="textSecondary" mt={2}>
          Files supported: {allowedExtensions.join(", ").toUpperCase()}{" "}
        </Typography>
        <Typography variant="caption" color="textSecondary" mt={2}>
          Maximum Size: {maxFileSizeMB} MB
        </Typography>
      </Box>
    </Box>
  );
};
